import { TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BookingRulesType } from '../../api/types/response/booking-rule.response';
import { bookingRulesTypeTranslations } from '../../constants/booking-rules-type-translations';

export interface BookingRulesActionsSlotPayload {
  token: string;
  name: string;
  employeesGroupsTokens: string[];
}

interface Props {
  token: string;
  name: string;
  type: BookingRulesType;
  employeesGroupsTokens: string[];
  employeesGroupsNames: string[];
  actionsSlot: (payload: BookingRulesActionsSlotPayload) => ReactNode;
}

export const BookingRulesTableRow = ({
  token,
  name,
  type,
  employeesGroupsTokens,
  employeesGroupsNames,
  actionsSlot,
}: Props) => {
  const { t } = useTranslation(['bookingRules', 'common']);

  const navigate = useNavigate();
  const location = useLocation();

  const renderEmployeesGroupsNames = () => {
    switch (true) {
      case employeesGroupsNames.length === 1:
        return (
          <Typography variant="body2">{employeesGroupsNames[0]}</Typography>
        );

      case employeesGroupsNames.length > 1:
        return (
          <Tooltip
            title={employeesGroupsNames.map(groupName => (
              <Typography key={groupName} variant="body2">
                {groupName}
              </Typography>
            ))}>
            <span>
              <Typography
                variant="body2"
                sx={{
                  textDecoration: 'underline',
                }}>{`${employeesGroupsNames.length} ${t('bookingRules:common.groups')}`}</Typography>
            </span>
          </Tooltip>
        );

      default:
        return (
          <Typography variant="body2" color="textSecondary">
            {t('common:notAssigned')}
          </Typography>
        );
    }
  };

  return (
    <TableRow
      hover
      sx={{ cursor: 'pointer' }}
      onClick={() =>
        navigate({
          to: '/tenant/booking-rules/$bookingRulesToken',
          params: { bookingRulesToken: token },
          state: {
            backUrl: location.href,
          },
        })
      }>
      <TableCell>
        <Typography variant="body2">{name}</Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2">
          {t(bookingRulesTypeTranslations[type])}
        </Typography>
      </TableCell>

      <TableCell>{renderEmployeesGroupsNames()}</TableCell>

      <TableCell align="right">
        {actionsSlot({ token, name, employeesGroupsTokens })}
      </TableCell>
    </TableRow>
  );
};
